import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Download, FileText, Building, Vote, Users, Trash2, Bus } from 'lucide-react';
import { News, Event, Service } from '@/lib/index';
import { springPresets } from '@/lib/motion';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  FileText,
  Building,
  Vote,
  Users,
  Trash2,
  Bus,
};

interface NewsCardProps {
  news: News;
  featured?: boolean;
}

export function NewsCard({ news, featured = false }: NewsCardProps) {
  const cardClasses = featured
    ? 'group relative overflow-hidden rounded-2xl bg-card border border-border shadow-lg hover:shadow-xl transition-all duration-300'
    : 'group relative overflow-hidden rounded-xl bg-card border border-border shadow-sm hover:shadow-md transition-all duration-300';

  const imageClasses = featured
    ? 'aspect-[16/9] w-full object-cover group-hover:scale-105 transition-transform duration-500'
    : 'aspect-[4/3] w-full object-cover group-hover:scale-105 transition-transform duration-500';

  return (
    <motion.article
      className={cardClasses}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springPresets.gentle}
      whileHover={{ y: -4 }}
    >
      <div className="relative overflow-hidden">
        <img src={news.image} alt={news.title} className={imageClasses} />
        <div className="absolute top-4 left-4">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-primary text-primary-foreground rounded-full">
            {news.category}
          </span>
        </div>
      </div>
      <div className={featured ? 'p-8' : 'p-6'}>
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <Calendar className="w-4 h-4" />
          <time dateTime={news.date}>{new Date(news.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</time>
        </div>
        <h3 className={featured ? 'text-2xl font-semibold mb-3 text-foreground group-hover:text-primary transition-colors' : 'text-xl font-semibold mb-2 text-foreground group-hover:text-primary transition-colors'}>
          {news.title}
        </h3>
        <p className="text-muted-foreground line-clamp-3 mb-4">{news.excerpt}</p>
        <Link
          to={`/actualites/${news.id}`}
          className="inline-flex items-center text-sm font-medium text-primary hover:text-primary/80 transition-colors"
        >
          Lire la suite
          <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </motion.article>
  );
}

interface EventCardProps {
  event: Event;
}

export function EventCard({ event }: EventCardProps) {
  return (
    <motion.article
      className="group relative overflow-hidden rounded-xl bg-card border border-border shadow-sm hover:shadow-md transition-all duration-300"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springPresets.gentle}
      whileHover={{ y: -4 }}
    >
      <div className="relative overflow-hidden aspect-[16/9]">
        <img src={event.image} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-4 left-4">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-accent text-accent-foreground rounded-full">
            {event.category}
          </span>
        </div>
      </div>
      <div className="p-6">
        <div className="flex flex-col gap-2 mb-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <time dateTime={event.date}>
              {new Date(event.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              {event.time && ` à ${event.time}`}
            </time>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span>{event.location}</span>
          </div>
        </div>
        <h3 className="text-xl font-semibold mb-2 text-foreground group-hover:text-primary transition-colors">
          {event.title}
        </h3>
        <p className="text-muted-foreground line-clamp-2">{event.description}</p>
      </div>
    </motion.article>
  );
}

interface ServiceCardProps {
  service: Service;
}

export function ServiceCard({ service }: ServiceCardProps) {
  const IconComponent = iconMap[service.icon];
  const content = (
    <>
      <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 text-primary mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
        {IconComponent ? <IconComponent className="w-7 h-7" /> : null}
      </div>
      <h3 className="text-lg font-semibold mb-2 text-foreground group-hover:text-primary transition-colors">
        {service.title}
      </h3>
      <p className="text-sm text-muted-foreground">{service.description}</p>
    </>
  );

  const cardClasses = "group relative overflow-hidden rounded-xl bg-card border border-border p-6 shadow-sm hover:shadow-md transition-all duration-300";

  if (service.link) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springPresets.gentle}
        whileHover={{ y: -4 }}
      >
        <Link to={service.link} className={cardClasses}>
          {content}
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.article
      className={cardClasses}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springPresets.gentle}
      whileHover={{ y: -4 }}
    >
      {content}
    </motion.article>
  );
}

interface PublicationCardProps {
  title: string;
  issue: string;
  image: string;
  downloadUrl: string;
}

export function PublicationCard({ title, issue, image, downloadUrl }: PublicationCardProps) {
  return (
    <motion.article
      className="group relative overflow-hidden rounded-xl bg-card border border-border shadow-sm hover:shadow-md transition-all duration-300"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springPresets.gentle}
      whileHover={{ y: -4 }}
    >
      <div className="relative overflow-hidden aspect-[3/4]">
        <img src={image} alt={`${title} - ${issue}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-1 text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground mb-4">{issue}</p>
        <a
          href={downloadUrl}
          download
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Download className="w-4 h-4" />
          Télécharger
        </a>
      </div>
    </motion.article>
  );
}
